import dayjs from 'dayjs';

import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { ISymbolDetail } from '@aqx-core/common/api/market';
import { EOrderIndicate, IOrder, IPendingOrder } from '@aqx-core/common/api/order';
import { DATETIME_SECONDS_FORMAT, getSecondsSinceEpoch } from '@aqx-core/common/dayjs';
import { convertToDisplayUTC } from '@aqx-core/common/hooks/timezone/use-display-utc-time';
import { TMixpanelProperties } from '@aqx-core/common/mixpanel/types';
import { accountService } from '@aqx-core/common/services/account';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { equityChartService } from '@aqx-core/common/services/trade/analysis/equity-chart';
import { EUIBuySellType, IUITradeFormData } from '@aqx-core/common/services/trade/form/types';
import {
  getOrderBuySellType,
  mapOrderBuySellTypeToTextUpperCase,
  mapOrderTypeToOrderTypeLabel
} from '@aqx-core/common/utils/order';
import { mapFillPolicyIntToText } from '@aqx-core/common/utils/trade-form';

import { IMixpanelConfirmEditOrderClickProps, IMixpanelTrackOrderClickProps, ITrackOrderProps } from '../../order/type';
import { EMixpanelPositionModuleTab, EMixpanelProperties, EMixpanelTrackFunction, ETradePreference } from '../../types';
import userHelper from '../UserHelper';
import {
  ICreateEditLimitOrderPropterties,
  ICreateEditMarketOrderPropterties,
  ICreatePlaceOrderClickPropertiesProps,
  ICreateTrackedOrderPropertiesProps,
  IPlaceOrderDetailsButtonClickProperties
} from './type';

class PlaceOrderHelper {
  public createFullChartProperties = (): TMixpanelProperties => {
    const isFullScreen = equityChartService.getIsFullScreen();
    return {
      [EMixpanelProperties.IS_FULL_CHART]: isFullScreen
    };
  };

  public createPlaceOrderClickProperties = ({
    symbol,
    direction,
    orderType
  }: ICreatePlaceOrderClickPropertiesProps): TMixpanelProperties => {
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      [EMixpanelProperties.SYMBOL]: symbol,
      [EMixpanelProperties.DIRECTION]: direction,
      [EMixpanelProperties.ORDER_TYPE]: orderType,
      ...fullChartProperties,
      ...userAccountProperties
    };
  };

  public createTradePreferenceProperties = (): TMixpanelProperties => {
    const isOneClickTradingEnabled = accountService.getIsOneClickTradingEnabled();
    let tradePreference = ETradePreference.TWO_CLICK_TRADING;
    if (isOneClickTradingEnabled) {
      tradePreference = ETradePreference.ONE_CLICK_TRADING;
    }
    return {
      [EMixpanelProperties.TRADE_PREFERENCE]: tradePreference
    };
  };

  public createBuySellDirectionProperties = (buySellType: EUIBuySellType): TMixpanelProperties => {
    return {
      [EMixpanelProperties.DIRECTION]: mapOrderBuySellTypeToTextUpperCase(buySellType, 'en_GB')
    };
  };

  // eslint-disable-next-line complexity
  private createOrderProperties = (
    values: Partial<IUITradeFormData>,
    symbolDetails: ISymbolDetail,
    displayTime: string
  ): TMixpanelProperties => {
    const { symbol, contractSize, ask, bid, currency, marginInitial, swapLong, swapShort, stopsLevel } =
      symbolDetails || {};
    const { lotSize, fillPolicy, stopLoss, takeProfit, price, priceTrigger, tradeExpiry, expiration, orderType } =
      values || {};

    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? mapFillPolicyIntToText(fillPolicy, 'en_GB') : '-',
      [EMixpanelProperties.INDICATE]: EOrderIndicate.PRICE,
      [EMixpanelProperties.LOT_SIZE]: lotSize ?? '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ?? '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ?? '-',
      [EMixpanelProperties.ORDER_TYPE]:
        orderType !== null && orderType !== undefined ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.ASK]: ask ?? '-',
      [EMixpanelProperties.BID]: bid ?? '-',
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency,
      [EMixpanelProperties.CONTRACT_SIZE]: contractSize,
      [EMixpanelProperties.PRICE]: price ? Number(price) : '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ?? '-',
      [EMixpanelProperties.TRADE_EXPIRY]: tradeExpiry ?? '-',
      [EMixpanelProperties.EXPIRATION]: expiration
        ? convertToDisplayUTC(dayjs(expiration).format(DATETIME_SECONDS_FORMAT))
        : '-',
      [EMixpanelProperties.DISPLAY_TIME]: displayTime ?? '-',
      [EMixpanelProperties.INITIAL_MARGIN]: marginInitial ?? '-',
      [EMixpanelProperties.SWAP_LONG]: swapLong ?? '-',
      [EMixpanelProperties.SWAP_SHORT]: swapShort ?? '-',
      [EMixpanelProperties.MIN_STOP_DISTANCE]: stopsLevel ?? '-'
    };
  };

  public createPlaceOrderSubmittedProperties = ({
    values,
    symbolDetails,
    buySellType,
    displayTime
  }: ITrackOrderProps) => {
    const buySellTypeProperties = this.createBuySellDirectionProperties(buySellType);
    const orderDetailsProperties = this.createOrderProperties(values, symbolDetails, displayTime);
    const orderUserProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();
    const fullChartProperties = this.createFullChartProperties();
    return {
      ...fullChartProperties,
      ...orderDetailsProperties,
      ...buySellTypeProperties,
      ...orderUserProperties,
      ...tradePreference
    };
  };

  public createPlaceOrderDetailsButtonClickProperties = ({
    symbol,
    modalType
  }: IPlaceOrderDetailsButtonClickProperties) => {
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();
    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.MODAL_TYPE]: modalType ?? '-',
      ...fullChartProperties,
      ...userAccountProperties,
      ...tradePreference
    };
  };

  // eslint-disable-next-line complexity
  private createEditMarketOrderProperties = ({ values, order, livePrice }: ICreateEditMarketOrderPropterties) => {
    const { stopLoss, takeProfit } = values || {};
    const {
      symbol,
      currency,
      openTime,
      orderType,
      units,
      openPrice,
      currentPrice,
      swap,
      commission,
      fillPolicy,
      lotSize: maxLotSize
    } = order;

    const convertedOpenTime = getSecondsSinceEpoch(
      companyViewController.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );
    return {
      [EMixpanelProperties.DIRECTION]: mapOrderBuySellTypeToTextUpperCase(getOrderBuySellType(orderType), 'en_GB'),
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]:
        orderType !== undefined || orderType !== null ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: maxLotSize ? maxLotSize : '-',
      [EMixpanelProperties.FILL_POLICY]:
        fillPolicy !== undefined || fillPolicy !== null ? mapFillPolicyIntToText(fillPolicy, 'en_GB') : '-',
      [EMixpanelProperties.OPEN_PRICE]: openPrice ? openPrice : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? livePrice : (currentPrice ?? '-'),
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ?? '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ?? '-',
      [EMixpanelProperties.SWAP]: swap ? swap : '-',
      [EMixpanelProperties.COMMISSION]: commission ? commission : '-',
      [EMixpanelProperties.UNITS]: units ? units : '-'
    };
  };

  // eslint-disable-next-line complexity
  public createEditLimitOrderProperties = ({ values, order, livePrice }: ICreateEditLimitOrderPropterties) => {
    const {
      price, // Entry Price
      priceTrigger, // Stop Limit Price / Pending Price
      stopLoss,
      takeProfit,
      tradeExpiry,
      expiration
    } = values;

    const { lotSize, fillPolicy, symbol, openTime, orderType, units, currentPrice, filledLotSize } = order;

    const isMT5 = companyViewController.getCompanyIntegration() === EMainProductCodeType.METATRADER5;

    const displayLotSize = () => {
      const filled = Number.isFinite(lotSize) ? lotSize : '-';
      if (isMT5) {
        const unfill = Number.isFinite(filledLotSize) ? filledLotSize : '-';
        return `${filled} / ${unfill}`;
      }
      return filled;
    };

    const convertedOpenTime = getSecondsSinceEpoch(
      companyViewController.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );

    const convertedExpiration = getSecondsSinceEpoch(
      companyViewController.convertToDayjsWithOffset({ input: expiration, keepTime: true })
    );

    return {
      [EMixpanelProperties.DIRECTION]: mapOrderBuySellTypeToTextUpperCase(getOrderBuySellType(orderType), 'en_GB'),
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]:
        orderType !== undefined || orderType !== null ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: displayLotSize() ?? '-',
      [EMixpanelProperties.FILL_POLICY]:
        fillPolicy !== undefined || fillPolicy !== null ? mapFillPolicyIntToText(fillPolicy, 'en_GB') : '-',
      [EMixpanelProperties.EXPIRATION]: expiration
        ? convertToDisplayUTC(dayjs(convertedExpiration).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.TRADE_EXPIRY]: tradeExpiry ? tradeExpiry : '-',
      [EMixpanelProperties.PRICE]: price ?? '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ?? '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? livePrice : (currentPrice ?? 0),
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ?? '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ?? '-',
      [EMixpanelProperties.UNITS]: units ? units : '-'
    };
  };

  public createConfirmEditOrderClickProperties = ({
    positionModuleTab,
    order,
    values,
    livePrice
  }: IMixpanelConfirmEditOrderClickProps) => {
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();

    let orderProperties = {};
    if (positionModuleTab === EMixpanelPositionModuleTab.OPEN_POSITIONS) {
      orderProperties = this.createEditMarketOrderProperties({
        values,
        order: order as IOrder,
        livePrice
      });
    } else if (positionModuleTab === EMixpanelPositionModuleTab.PENDING_ORDERS) {
      orderProperties = this.createEditLimitOrderProperties({
        values,
        order: order as IPendingOrder,
        livePrice
      });
    }
    return {
      [EMixpanelProperties.POSITION_MODULE_TAB]: positionModuleTab ?? '-',
      ...fullChartProperties,
      ...userAccountProperties,
      ...tradePreference,
      ...orderProperties
    };
  };

  /** @ToDo Add TP SL and PL */
  private createTrackedOrderProperties = ({
    trackOrderParams,
    livePrice,
    priceTrigger
  }: ICreateTrackedOrderPropertiesProps) => {
    const { symbol, orderId, orderType, stopLoss, takeProfit, entryPrice } = trackOrderParams;
    // const profitTrackingOrders = orderService.getProfitTrackingOrders();
    // const currentOrderWithLiveProfit = profitTrackingOrders.find((o) => o.orderId === orderId);
    // const profitLoss = currentOrderWithLiveProfit?.profit;
    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.ORDER_ID]: orderId ?? '-',
      [EMixpanelProperties.DIRECTION]: mapOrderBuySellTypeToTextUpperCase(getOrderBuySellType(orderType), 'en_GB'),
      [EMixpanelProperties.STOP_LOSS]: stopLoss ? stopLoss : '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ? takeProfit : '-',
      [EMixpanelProperties.ENTRY_PRICE]: entryPrice ? entryPrice : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? livePrice : '-',
      // [EMixpanelProperties.PROFIT_LOSS]: profitLoss ? profitLoss : '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ? priceTrigger : '-'
    };
  };

  public createTracKOrderClickProperties = ({
    trackOrderParams,
    livePrice,
    positionModuleTab,
    isTracking,
    priceTrigger
  }: IMixpanelTrackOrderClickProps) => {
    const trackedOrder = this.createTrackedOrderProperties({ trackOrderParams, livePrice, priceTrigger });
    const fullChartProperties = this.createFullChartProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const tradePreference = this.createTradePreferenceProperties();
    return {
      [EMixpanelProperties.POSITION_MODULE_TAB]: positionModuleTab ?? '-',
      [EMixpanelProperties.FUNCTION]: isTracking ? EMixpanelTrackFunction.TRACK : EMixpanelTrackFunction.UNTRACK,
      ...fullChartProperties,
      ...userAccountProperties,
      ...tradePreference,
      ...trackedOrder
    };
  };
}

export const placeOrderHelper = new PlaceOrderHelper();
export default placeOrderHelper;
